import { useMemo } from 'react';

import { useDispatch } from 'react-redux';

import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { useDidMount } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { saveUserInput, useServiceResponse, useUserInput } from '@store';
import { Account, CompanyAccounts, SelectedAccount } from '@types';

interface AccountSelectorHelperResources {
	contractLinkedAccount: string;
	selectAccountLinkedToContract: string;
	availableBalance: string;
	accountableBalance: string;
	accountWithoutIntegratedSolution: string;
}

interface AccountSelectorHelperOutput {
	resources: AccountSelectorHelperResources;
	companyAccounts: CompanyAccounts[];
	onlyOneAccountResource: string;
	storeSelectedAccount: SelectedAccount | undefined;
	hasOnlyOneAccount: boolean;
	hasGlobalSolutionIntegrated: boolean;
	handleSelectAccount: (selectedAccount: SelectedAccount) => void;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const useAccountSelectorHelper = (): AccountSelectorHelperOutput => {
	const { retrieveAccounts } = useServiceResponse();
	const { solutionIntegrated, selectedAccount: storeSelectedAccount } = useUserInput();
	const dispatch = useDispatch();
	const t = useTranslation();

	const hasGlobalSolutionIntegrated = solutionIntegrated ?? false;

	const resources: AccountSelectorHelperResources = {
		contractLinkedAccount: t('placeholders.accountSelector.title.contractLinkedAccount'),
		selectAccountLinkedToContract: t('placeholders.accountSelector.title.selectAccountLinkedToContract'),
		availableBalance: t('placeholders.accountSelector.details.label.availableBalance'),
		accountableBalance: t('placeholders.accountSelector.details.label.accountableBalance'),
		accountWithoutIntegratedSolution: t('placeholders.accountSelector.alert.accountWithoutIntegratedSolution'),
	};

	const setSelectedAccountWithIntegrationSolution = (companyAccounts: CompanyAccounts) => {
		if (companyAccounts.accounts?.some((account) => account.integratedSolution)) {
			const accountWithSolutionIntegration = companyAccounts.accounts?.filter(
				(account) => account.integratedSolution
			)[0];
			dispatch(
				saveUserInput({
					selectedAccount: {
						account: accountWithSolutionIntegration,
						companyName: companyAccounts.companyName,
						companyCis: companyAccounts.companyCis,
						companyNif: companyAccounts.companyNif,
					},
				})
			);
		}
	};

	useDidMount(() => {
		if (!storeSelectedAccount) {
			if (hasOnlyOneAccount) {
				dispatch(
					saveUserInput({
						selectedAccount: {
							account: companyAccounts[0].accounts[0],
							companyName: companyAccounts[0].companyName,
							companyCis: companyAccounts[0].companyCis,
							companyNif: companyAccounts[0].companyNif,
						},
					})
				);
			} else if (companyAccounts.length > 0 && !allAccountsHaveIntegratedSolution) {
				companyAccounts.forEach((companyAccount) => {
					setSelectedAccountWithIntegrationSolution(companyAccount);
				});
			}
		}
	});

	let allAccountsHaveIntegratedSolution = false;
	const companyAccounts: CompanyAccounts[] =
		retrieveAccounts?.entityOut?.clientAccountStructureList?.map((company) => {
			allAccountsHaveIntegratedSolution = company.accountList?.accountList?.every(
				(account) => account.integratedSolution
			) as boolean;
			return {
				companyName: company.name as string,
				companyNif: company.nif as string,
				companyCis: company.cis as string,
				accounts:
					company.accountList?.accountList?.map((account) => {
						return {
							id: account.id,
							name: account.name,
							number: account.accountNumber,
							accountableBalance: account.balance,
							availableBalance: account.availableBalance,
							iban: account.iban,
							openBankingPrefixImageUrl: account.openBankingPrefixImageUrl,
							integratedSolution: account.integratedSolution,
						} as Account;
					}) || [],
			};
		}) || [];

	const hasOnlyOneAccount = useMemo(() => {
		return companyAccounts.length === 1 && companyAccounts[0].accounts.length === 1;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyAccounts.length]);

	const onlyOneAccountResource = hasOnlyOneAccount
		? resources.contractLinkedAccount
		: resources.selectAccountLinkedToContract;

	const handleSelectAccount = (selectedAccount: SelectedAccount) => {
		storeSelectedAccount?.account.id === selectedAccount?.account.id
			? dispatch(
					saveUserInput({
						selectedAccount: undefined,
						acceptedTermsAndConditionsBank: undefined,
					})
			  )
			: dispatch(
					saveUserInput({
						selectedAccount: { ...selectedAccount },
					})
			  );
	};

	return {
		resources,
		companyAccounts,
		onlyOneAccountResource,
		storeSelectedAccount,
		hasOnlyOneAccount,
		hasGlobalSolutionIntegrated,
		handleSelectAccount,
	};
};
